import React, { useState, useCallback } from "react";
import { useIntl } from "react-intl";
import { Row, Col } from "antd";

import {
  getEntity,
  patchEntity,
  deleteEntity,
} from "../../../modules/crudOperations";
import DhlLogo from "../../app/generic/dhlLogo";
import { czechLocale } from "../../app/languages";
import LoadTable from "../../app/generic/loadTable";
import CustomIcon from "../../app/generic/customIcon";
import IconButton from "../../app/generic/iconButton";
import { isSuperUser } from "../../../modules/session";
import ResetPasswordForm from "../../user/resetPasswordForm";
import TableRowActions from "../../app/generic/tableRowActions";

function UserListTable({
  admin,
  users,
  short,
  onSetUser,
  loadUsers,
  superAdmin,
  superReader,
  currentUserId,
  onNewUserClick,
  customerService,
}) {
  const intl = useIntl();
  const [userToReset, setUserToReset] = useState();
  const [resetVisible, setResetVisible] = useState();
  const showResetForm = useCallback((user) => {
    setUserToReset(user);
    setResetVisible(true);
  }, []);

  const handleEditClick = useCallback(
    (u) => {
      if ((admin && !u.admin) || superAdmin) {
        getEntity("user", u.key, undefined, onSetUser);
        onNewUserClick();
      }
    },
    [admin, onSetUser, superAdmin, onNewUserClick]
  );

  const dropUser = useCallback(
    (userId) => {
      deleteEntity(
        "user",
        userId,
        intl.formatMessage({ id: "user", defaultMessage: "Uživatel" }),
        intl.formatMessage({
          id: "user.errorName",
          defaultMessage: "uživatele",
        }),
        loadUsers
      );
    },
    [intl, loadUsers]
  );

  const blockUser = useCallback(
    (user) => {
      patchEntity(
        "user",
        user.key,
        { blocked: !user.blocked },
        intl.formatMessage({ id: "user", defaultMessage: "Uživatel" }),
        intl.formatMessage({
          id: "user.errorName",
          defaultMessage: "uživatele",
        }),
        undefined,
        loadUsers
      );
    },
    [intl, loadUsers]
  );

  const columns = [
    {
      key: "firstname",
      title: intl.formatMessage({
        id: "user.firstname",
        defaultMessage: "Jméno",
      }),
      dataIndex: "firstname",
    },
    {
      key: "lastname",
      title: intl.formatMessage({
        id: "user.lastname",
        defaultMessage: "Příjmení",
      }),
      dataIndex: "lastname",
    },
    {
      key: "username",
      title: intl.formatMessage({
        id: "user.username",
        defaultMessage: "Uživatelské jméno",
      }),
      dataIndex: "username",
    },
    {
      key: "email",
      title: intl.formatMessage({ id: "user.email", defaultMessage: "Email" }),
      dataIndex: "email",
    },
    {
      key: "admin",
      title: intl.formatMessage({
        id: "user.admin",
        defaultMessage: "Administrátor",
      }),
      dataIndex: "admin",
      width: 122,
      className: "center",
      render: (admin, user) => (
        <>
          <CustomIcon
            type={admin ? "check" : "close"}
            title={
              admin
                ? intl.formatMessage({
                    id: "user.admin.yes",
                    defaultMessage: "Uživatel má administrátorské práva",
                  })
                : intl.formatMessage({
                    id: "user.admin.no",
                    defaultMessage: "Uživatel bez administrátorských práv",
                  })
            }
          />
          {isSuperUser(user.userRole && user.userRole.name) && (
            <DhlLogo
              intl={intl}
              intlId="user.cs"
              defaultMessage="DHL zákaznický servis"
            />
          )}
        </>
      ),
    },
    {
      key: "blocked",
      title: intl.formatMessage({
        id: "user.blocked",
        defaultMessage: "Stav účtu",
      }),
      dataIndex: "blocked",
      width: intl.locale === czechLocale ? 95 : 120,
      className: "center",
      render: (blocked, user) => {
        const canBeBlocked =
          (admin && !user.admin) || superAdmin || customerService;
        return (
          <CustomIcon
            disabled={!canBeBlocked || short}
            onClick={() => canBeBlocked && blockUser(user)}
            color={blocked && "red"}
            type={blocked ? "stop" : "check"}
            title={
              blocked
                ? intl.formatMessage(
                    {
                      id: "user.blockedAccount",
                      defaultMessage: "Uživatelský účet je zablokován{suffix}.",
                    },
                    {
                      suffix: canBeBlocked
                        ? intl.formatMessage({
                            id: "user.enableAccount",
                            defaultMessage: ", kliknutím jej odblokujete",
                          })
                        : "",
                    }
                  )
                : intl.formatMessage(
                    {
                      id: "user.activedAccount",
                      defaultMessage: "Uživatelský účet je aktivní{suffix}.",
                    },
                    {
                      suffix: canBeBlocked
                        ? intl.formatMessage({
                            id: "user.disableAccount",
                            defaultMessage: ", kliknutím jej zablokujete",
                          })
                        : "",
                    }
                  )
            }
          />
        );
      },
    },
    {
      key: "lastLogin",
      title: intl.formatMessage({
        id: "user.lastLogin",
        defaultMessage: "Datum posledního přihlášení",
      }),
      dataIndex: "lastLogin",
    },
    ...(short
      ? []
      : [
          {
            key: "passwordChange",
            title: intl.formatMessage({
              id: "user.passwordChange",
              defaultMessage: "Datum změny hesla",
            }),
            dataIndex: "passwordChange",
          },
          {
            key: "passwordValidity",
            title: intl.formatMessage({
              id: "user.passwordValidity",
              defaultMessage: "Datum expirace hesla",
            }),
            dataIndex: "passwordValidity",
          },
          {
            key: "actions",
            title: "",
            dataIndex: "",
            render: (u) => {
              const disabled = (!admin || u.admin) && !superAdmin;
              return (
                <TableRowActions
                  disabled={disabled}
                  onDelete={() => dropUser(u.key)}
                  onEdit={() => handleEditClick(u)}
                >
                  <IconButton
                    icon="idcard"
                    title={intl.formatMessage({
                      id: "user.generatingPassword",
                      defaultMessage: "generování nového hesla",
                    })}
                    className="table-row-action-button"
                    onClick={() => showResetForm(u)}
                    disabled={
                      disabled &&
                      currentUserId !== u.key &&
                      !customerService &&
                      !superReader &&
                      !superAdmin
                    }
                  />
                </TableRowActions>
              );
            },
          },
        ]),
  ];

  return (
    <Row>
      <Col>
        <ResetPasswordForm
          user={userToReset}
          loadUsers={loadUsers}
          visible={resetVisible}
          setVisible={setResetVisible}
          currentUserId={currentUserId}
        />
        <LoadTable
          values={users}
          what={intl.formatMessage({
            id: "user.whom",
            defaultMessage: "uživatelů",
          })}
          columns={columns}
          className="no-pointer"
          onLoadValues={loadUsers}
          onRowDoubleClick={handleEditClick}
          emptyText={intl.formatMessage({
            id: "user.noUsers",
            defaultMessage: "tento klient nemá žádné uživatele",
          })}
        />
      </Col>
    </Row>
  );
}

export default UserListTable;
